import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, CheckCircle2, RefreshCw, AlertTriangle } from 'lucide-react';
import { formatApiError } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MonthlyPrizeTab = () => {
  const [pool, setPool] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr('');
    try {
      const [p1, p2] = await Promise.all([
        axios.get(`${API}/fantasy/prize-pool/current`),
        axios.get(`${API}/fantasy/admin/monthly-payouts`),
      ]);
      setPool(p1.data);
      setPayouts(p2.data || []);
    } catch (ex) { setErr(formatApiError(ex)); }
  };
  useEffect(() => { load(); }, []);

  const closeMonth = async () => {
    if (!window.confirm(`Encerrar mês ${pool.month}? Isto irá:\n\n1) Registar prémios de ${pool.net_pool.toFixed(2)}€ para o Top 3\n2) Reter comissão de ${pool.admin_fee.toFixed(2)}€\n3) ZERAR o ranking (total_points = 0) — não há reversão\n\nContinuar?`)) return;
    setBusy(true); setErr(''); setOk('');
    try {
      const { data } = await axios.post(`${API}/fantasy/admin/close-month`);
      setOk(`✅ Mês encerrado. Top 3 identificado para pagamento MB WAY.`);
      await load();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const markPaid = async (month, rank) => {
    if (!window.confirm(`Marcar prémio do ${rank}º lugar de ${month} como PAGO?`)) return;
    try {
      await axios.post(`${API}/fantasy/admin/monthly-payouts/mark-paid`, { month, rank });
      await load();
    } catch (ex) { alert(formatApiError(ex)); }
  };

  return (
    <div className="space-y-6">
      {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
      {ok && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">{ok}</div>}

      {pool && (
        <div className="bg-gradient-to-br from-emerald-950/60 to-slate-900/60 border border-emerald-400/30 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-white font-black text-lg">Mês actual: {pool.month}</div>
              <div className="text-emerald-200/70 text-xs">{pool.coaches} treinadores · {pool.gross_pool.toFixed(2)}€ bruto</div>
            </div>
            <button onClick={closeMonth} disabled={busy || pool.coaches === 0}
              className="bg-red-500 hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2"
              data-testid="close-month-btn">
              <AlertTriangle className="w-4 h-4" /> {busy ? 'A processar...' : 'Encerrar Mês e Processar Prémios'}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((rank) => (
              <div key={rank} className="bg-slate-900/60 rounded-xl p-3 text-center border border-white/5">
                <div className="text-slate-400 text-xs">{rank}º Lugar · {Math.round(pool.distribution[rank].pct * 100)}%</div>
                <div className="text-white font-black text-2xl mt-1 tabular-nums">{pool.distribution[rank].eur.toFixed(2)}€</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-white font-black text-sm mb-3 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-400" /> Histórico de Encerramentos
        </div>
        {payouts.length === 0 ? (
          <div className="text-slate-500 text-sm italic bg-slate-900/40 border border-white/5 rounded-xl p-4 text-center">
            Ainda não há meses encerrados.
          </div>
        ) : (
          <div className="space-y-3">
            {payouts.map((p) => (
              <div key={p._id} className="bg-slate-900/60 border border-white/10 rounded-xl p-4" data-testid={`payout-${p.month}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-white font-black">{p.month}</div>
                    <div className="text-slate-400 text-xs">{p.coaches} treinadores · {p.net_pool.toFixed(2)}€ líquido · {p.admin_fee.toFixed(2)}€ admin</div>
                  </div>
                  <div className="text-slate-500 text-xs">Fechado por {p.closed_by}</div>
                </div>
                <div className="space-y-1.5">
                  {p.top3.map((w) => (
                    <div key={w.rank} className="flex items-center justify-between bg-slate-800/40 rounded-lg px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`font-black w-6 text-center ${w.rank === 1 ? 'text-amber-400' : w.rank === 2 ? 'text-slate-300' : 'text-orange-400'}`}>{w.rank}º</span>
                        <div className="min-w-0">
                          <div className="text-white truncate">{w.user_name} <span className="text-slate-500 text-xs">({w.total_points.toFixed(1)} pts)</span></div>
                          <div className="text-slate-500 text-[11px] truncate">{w.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-emerald-300 font-black tabular-nums">{w.prize_eur.toFixed(2)}€</div>
                        {w.paid ? (
                          <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-full px-2 py-0.5 text-[10px] font-bold"><CheckCircle2 className="w-3 h-3" /> Pago</span>
                        ) : (
                          <button onClick={() => markPaid(p.month, w.rank)} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full px-3 py-0.5 text-[10px]" data-testid={`mark-paid-${p.month}-${w.rank}`}>Marcar pago</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyPrizeTab;
